import { useMemo, useEffect, useCallback, type MouseEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { fDateTime } from '../../../utils/formatTime';
import {
	Card,
	CardContent,
	CardActions,
	Stack,
	Grid,
	List,
	ListItem,
	ListItemText,
	Typography,
	Button,
	Box,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

import { useFormik, yupToFormErrors } from 'formik';

import { BulkRequest, isLocationOnsite, Test, Request } from '../../../models';
import { fCurrency } from '../../../utils/formatNumber';
import { PricingCalculator } from '../../../utils/pricingCalculator';
import { TestSelectionSkeleton } from '../../../components/shared';
import {
	useGetBulkRequestQuery,
	useGetBulkRequestPaymentSummaryQuery,
	useUpdateBulkRequestMutation,
	useCancelBulkRequestMutation,
} from '../../../slices/apis/bulkRequestApi';
import { useGetSuppliersQuery } from '../../../slices/apis/testingCentreApi';
import { useConfirm } from '../../../hooks/useConfirm';
import { useToast } from '../../../hooks/useToast';
import { useRequestRules } from '../../../hooks/useRequestRules';
import { usePermissions } from '../../../hooks/usePermissions';
import { BreadCrumbs } from '../../../components/nav-section';
import { BulkRequestLocation, BulkRequestTest } from '../steps';
import { PreferredAssessmentDates } from '../../request/steps';

export default function BulkRequestEdit() {
	const navigate = useNavigate();
	const { reqId } = useParams<{ reqId: string }>();
	const theme = useTheme();
	const { can } = usePermissions();
	const { data: bulkRequestData } = useGetBulkRequestQuery(reqId as string);
	const { data: paymentSummary } = useGetBulkRequestPaymentSummaryQuery(reqId as string, {
		skip: !can('req:read:payment'),
	});
	const { allowQtyEditing } = useRequestRules({ data: bulkRequestData, paymentSummary });
	const { data: testingCentres } = useGetSuppliersQuery();
	const [
		updateBulkRequest,
		{ isError: isUpdateRequestError, isLoading: isUpdateRequestLoading, isSuccess: isUpdateRequestSuccess },
	] = useUpdateBulkRequestMutation();
	const [
		cancelBulkRequest,
		{ isLoading: isCancelRequestLoading, isSuccess: isCancelRequestSuccess, isError: isCancelRequestError },
	] = useCancelBulkRequestMutation();
	const { Confirm, confirmProps } = useConfirm();
	const { showToast, ToastElement } = useToast();

	const initialValues = useMemo(() => {
		const defaults = BulkRequest.editSchema.getDefault();

		if (bulkRequestData && typeof bulkRequestData === 'object') {
			return BulkRequest.editSchema.cast(
				{
					...defaults,
					...bulkRequestData,
					tests: Test.applyDiscount(bulkRequestData.tests, paymentSummary?.bulkTestDiscounts.discountedTests ?? []),
				},
				{
					stripUnknown: true,
				},
			);
		}
		return defaults;
	}, [bulkRequestData, paymentSummary]);

	const formik = useFormik({
		initialValues,
		enableReinitialize: true,
		validate: async (values) => {
			const isOnsiteLocation = testingCentres ? isLocationOnsite(testingCentres, values.location) : false;
			const schema = BulkRequest.editSchema
				.concat(BulkRequest.testsSchema({ isOnsiteLocation }))
				.concat(BulkRequest.preferredAssessmentDateSchema);

			try {
				await schema.validate(values, { abortEarly: false });
				return {};
			} catch (err) {
				return yupToFormErrors(err);
			}
		},
		onSubmit: async (values) => {
			updateBulkRequest(BulkRequest.editSchema.cast(values, { stripUnknown: true }));
		},
	});

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	const onCancelRequest = () => {
		confirmProps.openConfirm();
	};

	useEffect(() => {
		if (isUpdateRequestSuccess) {
			showToast('Request has been updated successfully!', 'success');
		}
	}, [isUpdateRequestSuccess, showToast]);

	useEffect(() => {
		if (isUpdateRequestError) {
			showToast('Failed to update your request!', 'error');
		}
	}, [isUpdateRequestError, showToast]);

	useEffect(() => {
		if (isCancelRequestError) {
			showToast('Failed to cancel your request!', 'error');
		}
	}, [isCancelRequestError, showToast]);

	useEffect(() => {
		if (isCancelRequestSuccess) {
			navigate('/dashboard/bulk-requests');
		}
	}, [isCancelRequestSuccess, navigate]);

	if (!bulkRequestData || (can('req:read:payment') && !paymentSummary)) {
		return <TestSelectionSkeleton />;
	}

	const breadCrumbItems = [
		{
			text: 'My Bulk Requests',
			onClick: handleNavigate,
			dataRoute: '/dashboard/bulk-requests',
		},
		{
			text: `Request ${bulkRequestData.id}`,
		},
	];

	const price = new PricingCalculator(formik.values.tests);

	const amountDue = price.total() - Number(paymentSummary?.paidAmount ?? 0);
	const isPreferredAssessmentDateEditable = Request.isPreferredAssessmentTimeEditingAllowed(bulkRequestData.status);

	return (
		<>
			<Helmet>
				<title> Edit Bulk Request - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			{ToastElement}

			<form onSubmit={formik.handleSubmit}>
				<Stack spacing={2}>
					<Card>
						<CardContent>
							<Box sx={{ mb: 2 }}>
								<BreadCrumbs items={breadCrumbItems} />
							</Box>
							<Box
								sx={{
									'& .RequestTestTotalKey, & .RequestTestTotalValue': {
										fontSize: theme.typography.h4.fontSize,
									},
								}}
							>
								<BulkRequestTest formik={formik} allowQtyEditing={allowQtyEditing} />
							</Box>
						</CardContent>
					</Card>

					{paymentSummary && (
						<Card>
							<CardContent>
								<Stack spacing={2}>
									<Stack direction="row" justifyContent="space-between">
										<Typography variant="subtitle1" fontWeight="bold">
											Amount Paid
										</Typography>
										<Box sx={{ textAlign: 'right' }}>
											<Typography variant="subtitle1" sx={{ color: theme.palette.common.black }} fontWeight="bold">
												{fCurrency(paymentSummary.paidAmount)}
											</Typography>
										</Box>
									</Stack>
									<Stack direction="row" justifyContent="space-between">
										<Typography variant="h4" fontWeight="bold">
											Amount Due
										</Typography>
										<Box sx={{ textAlign: 'right' }}>
											<Typography variant="h4" sx={{ color: theme.palette.common.black }} fontWeight="bold">
												{fCurrency(Number(amountDue.toFixed(2)))}
											</Typography>
										</Box>
									</Stack>
								</Stack>
							</CardContent>
						</Card>
					)}

					<Card>
						<CardContent>
							{isPreferredAssessmentDateEditable ? (
								<PreferredAssessmentDates
									formik={formik}
									prefAssessmentDateLabels={[
										'Preferred Assessment Date',
										'Second Preferred Assessment Date',
										'Third Preferred Assessment Date',
									]}
								/>
							) : (
								<Box sx={{ mt: 2 }}>
									<Typography variant="body1" color="text.secondary" gutterBottom>
										Preferred Assessment Dates
									</Typography>
									{bulkRequestData.prefAssessmentDates.map((date) => (
										<Typography key={date?.toString()} variant="body1">
											{fDateTime(date)}
										</Typography>
									))}
								</Box>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardContent>
							<BulkRequestLocation formik={formik} />
						</CardContent>
						<CardActions sx={{ ml: 2 }}>
							<Stack direction="row" spacing={4} alignItems="center">
								<Button
									loading={isUpdateRequestLoading}
									variant="outlined"
									color="primary"
									type="submit"
									disabled={isUpdateRequestLoading}
								>
									<span>Update</span>
								</Button>

								<Button color="error" variant="outlined" data-route="/dashboard/bulk-requests" onClick={handleNavigate}>
									Back
								</Button>
							</Stack>
							<Grid container justifyContent="flex-end">
								<Grid>
									<Button
										loading={isCancelRequestLoading}
										variant="outlined"
										color="error"
										disabled={isUpdateRequestLoading || isCancelRequestLoading}
										onClick={onCancelRequest}
									>
										<span>Cancel Request</span>
									</Button>
								</Grid>
							</Grid>
						</CardActions>
					</Card>
				</Stack>
			</form>

			<Confirm
				{...confirmProps}
				title="Are you sure you want to cancel?"
				onConfirm={() => {
					confirmProps.closeConfirm();
					cancelBulkRequest(reqId);
				}}
			>
				<Typography variant="body1">Please note our cancellation policy:</Typography>

				<List dense>
					<ListItem sx={{ display: 'list-item', p: 0 }}>
						<ListItemText primary="Cancellations 7+ days prior to assessment receive a full refund." />
					</ListItem>

					<ListItem sx={{ display: 'list-item', p: 0 }}>
						<ListItemText primary="Cancellations made 2–7 days prior receive a 50% refund of the deposit." />
					</ListItem>

					<ListItem sx={{ display: 'list-item', p: 0 }}>
						<ListItemText primary="Cancellations within 48 hours are non-refundable." />
					</ListItem>
				</List>
			</Confirm>
		</>
	);
}
